<template>
    <v-input
        v-bind="{...$props, ...$attrs}"
        :value="value"
        label=""
    >
        <div class="container">
            <input
                type="file"
                ref="files"
                class="d-none"
                multiple
                @change="loadFiles"

            />
            <div
                v-if="multiple || !files.length"
                class="drag-area"
                :class="{ 'no-clickable': disabled, 'no-draggable': disabled && isDragging, 'drag-active': isDragging }"
                @dragover.prevent="onDragOver"
                @dragleave.prevent="onDragLeave"
                @drop.prevent="onDrop"
                @click="fileAction"
            >

                <v-icon class="mr-2">mdi-file</v-icon>
                <div :class="{ 'no-clickable': disabled, 'no-draggable': disabled && isDragging, 'drag-active': isDragging }" class="label-content text-center ">
                    <p>
                        {{ label }}
                    </p>
                    <p>
                        <small>
                            Formatos permitidos: {{ allowFileFormats.join(', ') }}
                        </small>
                    </p>

                </div>
            </div>

            <div v-if="!hideLoadedFiles" style="width: 100%;" class="d-flex flex-wrap">
                <v-card
                    v-for="(file, index) in files"
                    :key="index"
                    class="blue darken-1 ma-1 px-2 px-1 d-flex align-center justify-space-between white--text"
                    :class="{'full-width': !multiple}"
                >
                    <p class="my-0 button d-flex align-center">
                        <v-icon  v-bind="$attrs" class="white--text mr-2">
                            {{ fileIcon(file.name) }}
                        </v-icon>
                        {{ file.name }}
                    </p>
                    <v-btn
                        :loading="loading"
                        :disabled="disabled"
                        class="ml-2"
                        icon
                        :block="false"
                        @click="deleteFile(index)"
                    >
                        <v-icon class="white--text" >mdi-close</v-icon>
                    </v-btn>
                </v-card>
            </div>
        </div>
        <KEditor
            :dialog="showDialog"
            :loadedImages="toEditImages"
            @closeDialog="closeDialog"
            @sendImages="saveEditImages"
        />
    </v-input>
</template>

<script>
// COMPONENTS
import KEditor from '@/pages/Sinister/File/PhotosEditor/index'

// UTISL
import {
  readImage,
  reduceImageQuality,
  iterateDelayed,
  writeImage,
  fileIcon
} from '@/utils'

const isImage = ['png', 'jpg', 'jpeg', 'JPG', 'PNG', 'JPEG']

export default {
  name: 'KFileField',
  components: { KEditor },
  inheritAttrs: true,

  props: {
    exe: {
      type: Function,
      required: false
    },
    label: {
      type: String,
      required: false,
      default: 'Cargar archivo'
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    allowFileFormats: {
      type: Array,
      required: false,
      default: () => ['pdf']
    },
    btnClass: {
      type: String,
      required: false,
      default: 'info'
    },
    multiple: {
      default: false,
      type: Boolean,
      required: false
    },
    hideLoadedFiles: {
      default: false,
      type: Boolean,
      required: false
    },
    draggable: {
      type: Boolean,
      default: true
    },
    preloadedFiles: {
      type: Array,
      required: false
    },
    value: {
      required: true
    }
  },
  data() {
    return {
      loadingFile: false,
      isDragging: false,
      showDialog: false,
      files: [],
      toEditImages: [],
      fileIcon

    }
  },
  methods: {
    fileAction() {
      if (this.disabled) {
        return
      }
      if (this.exe) {
        this.exe()
        return
      }
      this.$refs.files.multiple = this.multiple
      this.$refs.files.click()
    },
    loadFiles(e) {
      const input = e.target
      const files = input ? input.files : []
      if (files.length === 0) return
      this.processFiles(this.multiple ? [...files] : [files[0]])
    },
    onDragOver(e) {
      e.preventDefault()
      this.isDragging = true
      e.dataTransfer.dropEffect = 'copy'
    },
    onDragLeave(e) {
      e.preventDefault()
      this.isDragging = false
    },
    onDrop(e) {
      if (this.draggable && !this.disabled) {
        e.preventDefault()
        this.isDragging = false
        const files = e.dataTransfer.files
        if (files.length === 0) return
        this.processFiles(this.multiple ? files : [files[0]])
      }
    },
    processFiles(files) {
      if (files.length <= 0) return

      let filteredFiles = []
      this.toEditImages = []
      let images = []
      if (
        files.some((f) => {
          const format = f.name.split('.').pop().toLowerCase()
          return !this.allowFileFormats.includes(format.toLowerCase())
        })
      ) {
        this.$alertMessage({
          title: 'Hay archivos de formatos no permitidos',
          text: `Formatos: ${this.allowFileFormats.join(', ')}`
        })
      }

      if (files.some((f) => f.size > 12000000)) {
        this.$alertMessage({
          title: 'Algunos archivos pesan más de 12 MB'
        })
      }
      for (let index = 0; index < files.length; index++) {
        const format = files[index].name.split('.').pop().toLowerCase()
        const size = files[index].size
        if (size > 12000000 || !this.allowFileFormats.includes(format.toLowerCase())) {
          continue
        }
        if (!isImage.includes(format)) {
          filteredFiles.push(files[index])
        } else {
          images.push(files[index])
        }
      }

      if (images.length) {
        this.loadImages(images)
      } else {
        this.files = [...this.files, ...filteredFiles]
        this.$emit('input', this.multiple ? this.files : this.files[0])
      }
      this.$refs.files.value = ''
    },
    loadImagesPromise(file) {
      const promise = new Promise((resolve) => {
        return readImage(file).then((base64) =>
          reduceImageQuality(base64).then((base64Reduced) =>
            writeImage(base64Reduced).then((fileReduced) => {
              let imgObject = {
                file: fileReduced,
                originalFile: file,
                src: base64Reduced,
                isImage: true
              }
              this.toEditImages.push(imgObject)
              resolve()
            })
          )
        )
      })
      return promise
    },
    loadImages(images) {
      return iterateDelayed(images, 50, this.loadImagesPromise)
        .then(() => (this.showDialog = !!this.toEditImages.length))
    },
    deleteFile(index) {
      if (this.multiple) {
        this.files.splice(index, 1)
        this.$emit('input', this.files)
        return
      }
      this.$emit('input', null)
      this.$refs.files.value = ''
      this.files = []
    },
    closeDialog(images) {
      if (this.showDialog) {
        this.saveEditImages(images)
      }
    },
    saveEditImages(images) {
      let files = images.map((i) => i.file)
      this.toEditImages = []
      this.showDialog = false
      this.files = [...this.files, ...files]
      this.$emit('input', this.multiple ? this.files : this.files[0])
    }
  },
  watch: {
    preloadedFiles(newValue) {
      if (newValue) {
        const files = Array.isArray(newValue) ? newValue : [newValue]
        this.processFiles(files)
      }
    },
    value(newValue) {
      if (!newValue) {
        this.files = []
        this.$refs.files.value = ''
      }
    }
  }

}
</script>

<style lang="less" scoped>

.drag-area {
  border-radius: 5px;
  width: 100%;
  min-height: 140px;
  height: 100%;

  background: #f4f3f9;
  color: #fe0000;
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: center;
  cursor: pointer;
  border: 2px dashed #0000001b;
  -webkit-user-select: none;
  .label-content{
    p { margin: 0;}
  }
}
.no-clickable {
  cursor: default;
  background-color: unset !important;
  border: unset !important;
  color: gray !important;
}
.no-draggable {
  border: unset !important;
  color: gray !important;
}

.drag-active {
  border: 2px dashed #00ff2a;
  background: #a0a0a0;
}

.drag-area .visible {
  font-size: 18px;
}

.select {
  color: #5256ad;
  margin-left: 5px;
  cursor: pointer;
  transition: 0.4s;
}

.select:hover {
  opacity: 0.6;
}

.drag-area .on-drop,
.drag-area.dragover .visible {
  display: none;
}

.drag-area .on-drop,
.drag-area.dragover .visible {
  border: 2px dashed #00ff66;
}
.drag-area:hover {
  background: #dfdfdf;
}
.container{
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.full-width {
  width: 100%;
}
.v-card {
  height: 50px;
}
</style>
